import type {
  Group,
  Paginated,
  Pharmacy,
  Trade,
  TradeInstance,
  TradeRequirement,
  TradeRequirementInstance,
  TradesInstanceStatus,
  User,
} from 'types/models';
import type { InstanceValidation } from 'types/models/instance_validation';
import type { MeaValidationBrief } from 'types/models/mea_validation_brief';
import type { SelloutExtractRequest } from 'types/models/sellout_extract_request';
import type { DateRange } from 'types/utils';
import type { Attachment } from '../attachment';

export type GetAdminTradesInstancesParams = {
  page: number;
  trade_id?: number;
  query?: string;
  status?: TradesInstanceStatus;
};

export type AdminTradeInstance = Pick<TradeInstance, 'id' | 'status'> & {
  pharmacy: Pick<Pharmacy, 'id' | 'name' | 'phone_number' | 'cip'> & {
    group: Pick<Group, 'name'> | null;
  };
  trade: Pick<Trade, 'title'>;
};

export type GetAdminTradesInstancesResponse = Paginated<{ trades_instances: AdminTradeInstance[] }>;

export type AdminTradeInstanceShow = TradeInstance & {
  pharmacy: Pharmacy & {
    group: Group | null;
  };
  trade: {
    sellout_requirement: TradeRequirement | null;
    picture_requirement: TradeRequirement | null;
  };
};

export type GetAdminTradesInstanceResponse = {
  trades_instance: AdminTradeInstanceShow & { has_trades_requirements_instances: boolean };
};

export type DeleteAdminTradeInstanceResponse = {
  success: boolean;
};

export const PanelistExpectedResultValues = [
  'mark_as_ongoing',
  'mark_as_done',
  'mark_as_expired',
  'mark_as_pharmacy_not_panelist_client_first_occurrence',
  'mark_as_pharmacy_not_panelist_client_retry_expired',
  'mark_as_missing_sellout_data_sharing_approval_first_occurrence',
  'mark_as_missing_sellout_data_sharing_approval_retry_expired',
  'mark_as_missing_approval_or_not_client_first_occurrence',
  'mark_as_missing_approval_or_not_client_retry_expired',
  'reset',
] as const;

export type PanelistExpectedResult = (typeof PanelistExpectedResultValues)[number];

export const PanelistErrorMessageValues = [
  'instance_not_found',
  'instance_not_subscribed',
  'sellout_panelist_request_already_exists',
  'trade_lgo_data_upload_disabled',
  'trade_without_sellout_requirement',
  'trade_without_trades_products',
  'pharmacy_without_lgo_activated',
] as const;

export type PanelistErrorMessage = (typeof PanelistErrorMessageValues)[number];

export type CreateFakePanelistRequestParams = {
  trades_instance_id: number;
  expected_result: PanelistExpectedResult;
};

export type CreateFakePanelistRequestResponse = {
  error_message?: PanelistErrorMessage;
  success?: boolean;
};

type ManualValidationValidation = Pick<
  InstanceValidation,
  'id' | 'status' | 'validation_date' | 'refused_reason'
> & {
  validated_by:
    | (Pick<User, 'id'> & {
        name: string;
      })
    | { name: string }
    | null;
};

type ManualValidationBrief = Pick<
  MeaValidationBrief,
  'additional_documents' | 'needs_additional_validation' | 'human_brief'
>;

type ManualValidationPreviousRefusal = Pick<InstanceValidation, 'refused_reason'> & {
  refused_at: string;
  proofs_unchanged_since_refusal: boolean;
};

type ManualValidationAiPrevalidation = Pick<InstanceValidation, 'status' | 'validation_reasoning'>;

type ManualValidationPharmacy = Pick<Pharmacy, 'id' | 'name' | 'cip' | 'address_string'> & {
  group: Pick<Group, 'name'> | null;
};

type ManualValidationRequirementInstance = Pick<TradeRequirementInstance, 'id'> & {
  pictures: Attachment[];
};

type ManualValidationSelloutExtraction = Pick<SelloutExtractRequest, 'status'> & {
  reasoning: string | null;
};

type ManualValidationSelloutRequirementInstance = ManualValidationRequirementInstance & {
  manual_validation_not_needed: boolean;
  // Null when nothing has run: the extraction lands after the participation is transmitted.
  extraction: ManualValidationSelloutExtraction | null;
};

export type GetManualValidationParticipationResponse = Pick<
  TradeInstance,
  'id' | 'trade_id' | 'status' | 'transmitted_at'
> & {
  validation: ManualValidationValidation | null;
  ai_prevalidation: ManualValidationAiPrevalidation | null;
  previous_refusal: ManualValidationPreviousRefusal | null;
  brief: ManualValidationBrief | null;
  pharmacy: ManualValidationPharmacy;
  picture_requirement_instance: ManualValidationRequirementInstance | null;
  sellout_requirement_instance: ManualValidationSelloutRequirementInstance | null;
};

export type GetManualValidationRefusalReasonsResponse = {
  mea: string[];
  sellout: string[];
};

export const participationValidationStatuses = ['validated', 'refused'] as const;
export type ParticipationValidationStatus = (typeof participationValidationStatuses)[number];

export const masterTradeParticipationStatuses = [
  'to_be_treated',
  'to_publish',
  'published',
] as const;
export type MasterTradeParticipationStatus = (typeof masterTradeParticipationStatuses)[number];

export type GetManualValidationMasterTradeParticipationsParams = {
  status: MasterTradeParticipationStatus;
  query?: string;
  validation_status?: ParticipationValidationStatus;
  transmitted_at_range?: DateRange;
};

export type MasterTradeParticipation = Pick<TradeInstance, 'id' | 'transmitted_at' | 'status'> & {
  pharmacy: ManualValidationPharmacy;
  validation: ManualValidationValidation | null;
};

export type GetManualValidationMasterTradeParticipationsResponse = {
  participations: MasterTradeParticipation[];
};

export type MarkAsRefusedParams = {
  refused_reason: string;
};
